import React, { useState, useEffect, useMemo } from "react";
import LZString from "lz-string";
import { fetchAndSaveFacebookAdSpend } from "./fbads";
import "./stats.css";

const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

const Stats4 = () => {
    const [tableData, setTableData] = useState([]);
    const [adsCosts, setAdsCosts] = useState({});
    const [isSyncing, setIsSyncing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [productCosts, setProductCosts] = useState({});
    const [debugInfo, setDebugInfo] = useState({});
    const [isFetchingCosts, setIsFetchingCosts] = useState(false);
    const [syncMessage, setSyncMessage] = useState("");
    const [expandedRows, setExpandedRows] = useState(new Set());

    // Load data from sessionStorage 
    useEffect(() => {
        const savedAdsCosts = sessionStorage.getItem("adsCosts");
        const savedProductCosts = sessionStorage.getItem("productCosts");
        
        setAdsCosts(savedAdsCosts ? JSON.parse(savedAdsCosts) : {});
        setProductCosts(savedProductCosts ? JSON.parse(savedProductCosts) : {});
    }, []);

    // Initial data load
    useEffect(() => {
        processOrdersFromStorage();
    }, []);

    // Process orders from storage
    const processOrdersFromStorage = async () => {
        setIsLoading(true);
        try {
            const compressedData = sessionStorage.getItem("rawOrdersData");
            if (!compressedData) {
                console.warn("No order data found in sessionStorage");
                setIsLoading(false);
                return;
            }
            
            const orders = JSON.parse(LZString.decompress(compressedData)) || [];
            console.log(`Processing ${orders.length} orders for profitability analysis`);
            
            processOrders(orders);
        } catch (error) {
            console.error("Error processing orders:", error);
            setIsLoading(false);
        }
    };

    // Fetch product costs
    const fetchProductCosts = async () => {
        setIsFetchingCosts(true);
        try {
            const costsResponse = await fetch(`${API_BASE_URL}/api/costs`);
            if (!costsResponse.ok) {
                throw new Error(`Failed to fetch product costs: ${costsResponse.status}`);
            }
            
            const costsData = await costsResponse.json();
            const costs = costsData.costs || {};
            
            setProductCosts(costs);
            sessionStorage.setItem("productCosts", JSON.stringify(costs));
            
            processOrdersFromStorage();
            
            alert("Product costs fetched and saved!");
        } catch (error) {
            console.error("Error fetching product costs:", error);
            alert("Failed to fetch product costs: " + error.message);
        } finally {
            setIsFetchingCosts(false);
        }
    };

    // Sync Facebook Ads Data
    const syncAds = async () => {
        setIsSyncing(true);
        setSyncMessage("");
        try {
            await fetchAndSaveFacebookAdSpend();
            
            const savedAdsCosts = sessionStorage.getItem("adsCosts");
            const freshAdsCosts = savedAdsCosts ? JSON.parse(savedAdsCosts) : {};
            setAdsCosts(freshAdsCosts);
            
            await processOrdersFromStorage();
            
            setSyncMessage("Ads data synced successfully!");
            setTimeout(() => setSyncMessage(""), 2000);
        } catch (error) {
            console.error("Error syncing ads data:", error);
            setSyncMessage("Sync failed!");
            setTimeout(() => setSyncMessage(""), 2000);
        } finally {
            setIsSyncing(false);
        }
    };

    // Calculate accurate refund amount for an order
    const calculateOrderRefundAmount = (order) => {
        let totalRefundAmount = 0;
        
        order.refunds?.forEach(refund => {
            refund.transactions?.forEach(transaction => {
                if (transaction.kind === "refund") {
                    totalRefundAmount += parseFloat(transaction.amount || 0);
                }
            });
        });
        
        return totalRefundAmount;
    };

    // Calculate COGS for delivered items only
    const calculateDeliveredCOGS = (order) => {
        let totalCOGS = 0;
        
        if (!order.line_items || order.financial_status === "voided") {
            return 0;
        }
        
        order.line_items.forEach(item => {
            const productId = item.product_id?.toString();
            const originalQuantity = parseInt(item.quantity) || 0;
            
            // Calculate refunded quantity for this specific item
            let refundedQuantity = 0;
            order.refunds?.forEach(refund => {
                refund.refund_line_items?.forEach(refundItem => {
                    if (refundItem.line_item_id === item.id) {
                        refundedQuantity += parseInt(refundItem.quantity) || 0;
                    }
                });
            });
            
            // Calculate delivered/kept quantity (what customer actually received and kept)
            const deliveredQuantity = Math.max(0, originalQuantity - refundedQuantity);
            
            if (deliveredQuantity > 0) {
                if (productId && productCosts[productId] && productCosts[productId].cost) {
                    const itemCost = parseFloat(productCosts[productId].cost) || 0;
                    totalCOGS += itemCost * deliveredQuantity;
                } else {
                    // Fallback: estimate 34% of item price
                    const itemPrice = parseFloat(item.price) || 0;
                    totalCOGS += (itemPrice * 0.34) * deliveredQuantity;
                }
            }
        });
        
        return totalCOGS;
    };

    // Main order processing function
    const processOrders = (orders) => {
        const dailyData = {};
        
        orders.forEach(order => {
            // Convert to Romania timezone
            const createdAtUTC = new Date(order.created_at);
            const romaniaOffset = 2 * 60;
            const createdAtRomania = new Date(createdAtUTC.getTime() + romaniaOffset * 60 * 1000);
            const creationDate = createdAtRomania.toISOString().split("T")[0];
            
            if (!dailyData[creationDate]) {
                dailyData[creationDate] = {
                    grossRevenue: 0,
                    refunds: 0,
                    storeCreditsUsed: 0,
                    cogs: 0,
                    taxes: 0,
                    shippingCosts: 0,
                    paymentFees: 0,
                    adsCost: 0,
                    orderCount: 0,
                    orderDetails: [] // Store detailed order info for expansion
                };
            }
            
            const dayData = dailyData[creationDate];
            
            dayData.orderCount += 1;
            
            // Calculate all metrics for this order
            const currentTotalPrice = parseFloat(order.current_total_price || 0); // Net amount after refunds
            const originalOrderValue = parseFloat(order.total_price || 0); // Original order value
            const refundAmount = calculateOrderRefundAmount(order);
            const orderCOGS = calculateDeliveredCOGS(order);
            const isVoided = order.financial_status === "voided";
            const isStoreCredit = order.payment_gateway_names?.includes("shopify_store_credit");
            const isCardPayment = order.shipping_lines?.some(shipping => 
                shipping.code === "Livrare Expres si Plata CARD"
            );
            
            // Calculate costs and profit per your business rules
            let totalShippingCost = 0;
            let paymentFees = 0;
            
            // Determine if this is a full refund (refund amount >= original order value)
            const isFullRefund = refundAmount >= originalOrderValue && refundAmount > 0;
            
            if (isVoided) {
                // Voided: shipping cost once (13.5 RON) - COD not picked up
                totalShippingCost = 13.5;
                paymentFees = 0;
            } else {
                // Regular order: standard shipping (13.5 RON)
                totalShippingCost = 13.5;
                
                // Payment fees for card transactions
                if (isCardPayment) {
                    paymentFees = (currentTotalPrice * 0.017) + 1.25; // 1.7% + 1.25 RON
                }
                
                if (refundAmount > 0) {
                    // Refunds: shipping twice (13.5 * 2 = 27 RON) + refund processing fee
                    totalShippingCost = 27;
                    paymentFees += 5; // Always 5 RON for refund processing
                }
            }
            
            // Revenue calculation: use current_total_price which is already net after refunds
            // Store credits don't count as actual revenue (no money in)
            const orderRevenue = isVoided ? 0 : (isStoreCredit ? 0 : currentTotalPrice);
            
            // COGS calculation: 0 for full refunds (items returned), calculated for partial/no refunds
            const adjustedCOGS = isFullRefund ? 0 : orderCOGS;
            
            // Tax calculation: 22% on realized income (excluding store credits and full refunds)
            const taxableIncome = isVoided || isFullRefund ? 0 : (isStoreCredit ? 0 : orderRevenue);
            const taxAmount22 = taxableIncome > 0 ? (taxableIncome * 0.215 / 1.215) : 0;
            
            // Order profit calculation (before ads)
            const orderProfit = orderRevenue - adjustedCOGS - taxAmount22 - totalShippingCost - paymentFees;
            
            // Store detailed order information
            dayData.orderDetails.push({
                id: order.id,
                name: order.name || `#${order.order_number}`,
                revenue: orderRevenue,
                refundAmount,
                isVoided,
                isStoreCredit,
                isCardPayment,
                isFullRefund,
                cogs: adjustedCOGS,
                taxes: taxAmount22,
                shipping: totalShippingCost,
                fees: paymentFees,
                orderProfit: orderProfit,
                status: order.financial_status,
                lineItemsCount: order.line_items?.length || 0
            });
            
            // Aggregate daily totals
            dayData.grossRevenue += orderRevenue;
            dayData.refunds += refundAmount;
            dayData.cogs += adjustedCOGS;
            dayData.taxes += taxAmount22;
            dayData.shippingCosts += totalShippingCost;
            dayData.paymentFees += paymentFees;
            
            if (isStoreCredit && !isVoided && !isFullRefund) {
                dayData.storeCreditsUsed += orderRevenue;
            }
        });
        
        // Add ads costs
        Object.keys(dailyData).forEach(date => {
            const dayData = dailyData[date];
            dayData.adsCost = adsCosts[date] || 0;
        });
        
        // Convert to table format
        const tableRows = Object.entries(dailyData)
            .map(([date, data]) => {
                // Profit before ads = Revenue - COGS - Taxes - Shipping - Payment Fees
                const profitBeforeAds = data.grossRevenue - data.cogs - data.taxes - data.shippingCosts - data.paymentFees;
                const profitAfterAds = profitBeforeAds - data.adsCost;
                const dateObj = new Date(date);
                const year = dateObj.getFullYear();
                const month = dateObj.getMonth(); // 0-indexed, 5 is June

                let fixedCost = 450;
                if (year > 2025 || (year === 2025 && month >= 6)) { // month >= 6 is July or later
                    fixedCost = 280;
                }
                const finalProfit = profitAfterAds - fixedCost;
                
                return {
                    date,
                    orderCount: data.orderCount,
                    revenue: data.grossRevenue,
                    cogs: data.cogs,
                    taxes: data.taxes,
                    shipping: data.shippingCosts,
                    fees: data.paymentFees,
                    profitBeforeAds: profitBeforeAds,
                    adsCost: data.adsCost,
                    profitAfterAds: profitAfterAds,
                    finalProfit: finalProfit,
                    orderDetails: data.orderDetails
                };
            })
            .sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setTableData(tableRows);
        setDebugInfo(dailyData);
        setIsLoading(false);
    };

    // Calculate totals
    const calculateTotals = useMemo(() => {
        return tableData.reduce((totals, row) => {
            Object.keys(totals).forEach(key => {
                if (typeof row[key] === 'number') {
                    totals[key] += row[key];
                }
            });
            return totals;
        }, {
            orderCount: 0,
            revenue: 0,
            cogs: 0,
            taxes: 0,
            shipping: 0,
            fees: 0,
            profitBeforeAds: 0,
            adsCost: 0,
            profitAfterAds: 0,
            finalProfit: 0
        });
    }, [tableData]);

    // Calculate monthly summaries
    const calculateMonthlySummary = useMemo(() => {
        const monthlyData = {};
        
        tableData.forEach((row) => {
            const date = new Date(row.date);
            const year = date.getFullYear();
            const month = date.getMonth();
            const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
            
                         if (!monthlyData[monthKey]) {
                 monthlyData[monthKey] = {
                     orderCount: 0,
                     revenue: 0,
                     cogs: 0,
                     taxes: 0,
                     shipping: 0,
                     fees: 0,
                     profitBeforeAds: 0,
                     adsCost: 0,
                     profitAfterAds: 0,
                     finalProfit: 0,
                     days: 0
                 };
             }
            
                         const monthData = monthlyData[monthKey];
             monthData.orderCount += row.orderCount;
             monthData.revenue += row.revenue;
             monthData.cogs += row.cogs;
             monthData.taxes += row.taxes;
             monthData.shipping += row.shipping;
             monthData.fees += row.fees;
             monthData.profitBeforeAds += row.profitBeforeAds;
             monthData.adsCost += row.adsCost;
             monthData.profitAfterAds += row.profitAfterAds;
             monthData.finalProfit += row.finalProfit;
             monthData.days += 1;
        });
        
        return monthlyData;
    }, [tableData]);

    // Toggle row expansion
    const toggleRowExpansion = (date) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(date)) {
            newExpanded.delete(date);
        } else {
            newExpanded.add(date);
        }
        setExpandedRows(newExpanded);
    };

    return (
        <div className="stats3-container">
            <h1 className="stats3-header">Daily Profitability Analysis (Stats4)</h1>
            
            <div className="button-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button className="button" onClick={syncAds} disabled={isSyncing}>
                        {isSyncing ? "Syncing Ads..." : "Sync Ads"}
                    </button>
                    {syncMessage && (
                        <span style={{ 
                            color: syncMessage.includes('failed') ? '#dc3545' : '#28a745',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}>
                            {syncMessage}
                        </span>
                    )}
                </div>
                <button className="button" onClick={processOrdersFromStorage}>
                    Recalculate
                </button>
                <button className="button" onClick={fetchProductCosts} disabled={isFetchingCosts}>
                    {isFetchingCosts ? "Fetching Costs..." : "Fetch Product Costs"}
                </button>
            </div>

            {isLoading ? (
                <div>Loading profitability analysis...</div>
            ) : (
                <>
                    <div className="monthly-summary" style={{ marginBottom: '20px' }}>
                        <h3>Monthly Summary</h3>
                        <table className="stats3-table">
                            <thead>
                                <tr>
                                    <th>Month</th>
                                    <th>Orders</th>
                                    <th>Revenue</th>
                                    <th>COGS</th>
                                    <th>Taxes</th>
                                    <th>Shipping</th>
                                    <th>Fees</th>
                                    <th>Profit Before Ads</th>
                                    <th>Ads</th>
                                    <th>Profit After Ads</th>
                                    <th>Final Profit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(calculateMonthlySummary)
                                    .sort((a, b) => new Date(b) - new Date(a))
                                    .map(monthKey => {
                                        const [year, month] = monthKey.split('-').map(Number);
                                        const monthData = calculateMonthlySummary[monthKey];
                                        return (
                                            <tr key={monthKey}>
                                                <td>{`${year.toString().slice(-2)}'-${String(month).padStart(2, '0')}`}</td>
                                                <td>{monthData.orderCount}</td>
                                                <td>{Math.round(monthData.revenue).toLocaleString()}</td>
                                                <td>{Math.round(monthData.cogs).toLocaleString()}</td>
                                                <td>{Math.round(monthData.taxes).toLocaleString()}</td>
                                                <td>{Math.round(monthData.shipping).toLocaleString()}</td>
                                                <td>{Math.round(monthData.fees).toLocaleString()}</td>
                                                <td style={{ fontWeight: 'bold', color: monthData.profitBeforeAds > 0 ? '#28a745' : '#dc3545' }}>
                                                    {Math.round(monthData.profitBeforeAds).toLocaleString()}
                                                </td>
                                                <td>{Math.round(monthData.adsCost).toLocaleString()}</td>
                                                <td style={{ fontWeight: 'bold', color: monthData.profitAfterAds > 0 ? '#28a745' : '#dc3545' }}>
                                                    {Math.round(monthData.profitAfterAds).toLocaleString()}
                                                </td>
                                                <td style={{ fontWeight: 'bold', color: monthData.finalProfit > 0 ? '#28a745' : '#dc3545' }}>
                                                    {Math.round(monthData.finalProfit).toLocaleString()}
                                                </td>
                                            </tr>
                                        );
                                    })
                                }
                            </tbody>
                        </table>
                    </div>

                    <div className="stats3-table-container">
                        <table className="stats3-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Orders</th>
                                    <th>Revenue</th>
                                    <th>COGS</th>
                                    <th>Taxes</th>
                                    <th>Shipping</th>
                                    <th>Fees</th>
                                    <th>Profit Before Ads</th>
                                    <th>Ads</th>
                                    <th>Profit After Ads</th>
                                    <th>Final Profit</th>
                                </tr>
                                <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                                    <td>TOTALS</td>
                                    <td>{calculateTotals.orderCount}</td>
                                    <td>{Math.round(calculateTotals.revenue).toLocaleString()}</td>
                                    <td>{Math.round(calculateTotals.cogs).toLocaleString()}</td>
                                    <td>{Math.round(calculateTotals.taxes).toLocaleString()}</td>
                                    <td>{Math.round(calculateTotals.shipping).toLocaleString()}</td>
                                    <td>{Math.round(calculateTotals.fees).toLocaleString()}</td>
                                    <td style={{ fontWeight: 'bold', color: calculateTotals.profitBeforeAds > 0 ? '#28a745' : '#dc3545' }}>
                                        {Math.round(calculateTotals.profitBeforeAds).toLocaleString()}
                                    </td>
                                    <td>{Math.round(calculateTotals.adsCost).toLocaleString()}</td>
                                    <td style={{ fontWeight: 'bold', color: calculateTotals.profitAfterAds > 0 ? '#28a745' : '#dc3545' }}>
                                        {Math.round(calculateTotals.profitAfterAds).toLocaleString()}
                                    </td>
                                    <td style={{ fontWeight: 'bold', color: calculateTotals.finalProfit > 0 ? '#28a745' : '#dc3545' }}>
                                        {Math.round(calculateTotals.finalProfit).toLocaleString()}
                                    </td>
                                </tr>
                            </thead>
                        <tbody>
                            {tableData.map((row, index) => (
                                <React.Fragment key={index}>
                                    <tr
                                        onClick={() => toggleRowExpansion(row.date)}
                                        style={{
                                            backgroundColor: row.finalProfit > 0 ? "#d4edda" : 
                                                           row.finalProfit < -200 ? "#f8d7da" : "white",
                                            cursor: "pointer"
                                        }}
                                    >
                                        <td>{expandedRows.has(row.date) ? '▼' : '▶'} {row.date}</td>
                                        <td>{row.orderCount}</td>
                                        <td>{Math.round(row.revenue).toLocaleString()}</td>
                                        <td>{Math.round(row.cogs).toLocaleString()}</td>
                                        <td>{Math.round(row.taxes).toLocaleString()}</td>
                                        <td>{Math.round(row.shipping).toLocaleString()}</td>
                                        <td>{Math.round(row.fees).toLocaleString()}</td>
                                        <td style={{ fontWeight: 'bold', color: row.profitBeforeAds > 0 ? '#28a745' : '#dc3545' }}>
                                            {Math.round(row.profitBeforeAds).toLocaleString()}
                                        </td>
                                        <td>{Math.round(row.adsCost).toLocaleString()}</td>
                                        <td style={{ fontWeight: 'bold', color: row.profitAfterAds > 0 ? '#28a745' : '#dc3545' }}>
                                            {Math.round(row.profitAfterAds).toLocaleString()}
                                        </td>
                                        <td style={{ fontWeight: 'bold', color: row.finalProfit > 0 ? '#28a745' : '#dc3545' }}>
                                            {Math.round(row.finalProfit).toLocaleString()}
                                        </td>
                                    </tr>
                                    {expandedRows.has(row.date) && (
                                        <tr>
                                            <td colSpan="11" style={{ padding: '0', backgroundColor: '#f8f9fa' }}>
                                                <div style={{ padding: '10px', fontSize: '12px' }}>
                                                    <table style={{ width: '100%', fontSize: '11px' }}>
                                                        <thead>
                                                            <tr style={{ backgroundColor: '#e9ecef' }}>
                                                                <th>Order</th>
                                                                <th>Revenue</th>
                                                                <th>COGS</th>
                                                                <th>Taxes</th>
                                                                <th>Shipping</th>
                                                                <th>Fees</th>
                                                                <th>Profit</th>
                                                                <th>Type</th>
                                                                <th>Items</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {row.orderDetails.map((order, orderIndex) => (
                                                                <tr 
                                                                    key={orderIndex}
                                                                    style={{
                                                                        borderBottom: orderIndex < row.orderDetails.length - 1 ? '1px solid #dee2e6' : 'none',
                                                                        backgroundColor: order.orderProfit > 0 ? '#e8f5e8' : 
                                                                                       order.orderProfit < -20 ? '#ffe6e6' : 'white'
                                                                    }}
                                                                >
                                                                    <td style={{ padding: '6px 8px' }}>{order.name}</td>
                                                                    <td style={{ padding: '6px 8px' }}>{Math.round(order.revenue).toLocaleString()}</td>
                                                                    <td style={{ padding: '6px 8px' }}>{Math.round(order.cogs).toLocaleString()}</td>
                                                                    <td style={{ padding: '6px 8px' }}>{Math.round(order.taxes).toLocaleString()}</td>
                                                                    <td style={{ padding: '6px 8px' }}>{Math.round(order.shipping).toLocaleString()}</td>
                                                                    <td style={{ padding: '6px 8px' }}>{Math.round(order.fees).toLocaleString()}</td>
                                                                    <td style={{ 
                                                                        padding: '6px 8px',
                                                                        fontWeight: 'bold',
                                                                        color: order.orderProfit > 0 ? '#28a745' : order.orderProfit < -20 ? '#dc3545' : '#6c757d'
                                                                    }}>
                                                                        {Math.round(order.orderProfit).toLocaleString()}
                                                                    </td>
                                                                    <td style={{ padding: '6px 8px' }}>
                                                                        <span style={{ fontSize: '10px' }}>
                                                                            {order.isVoided ? 'Void' : 
                                                                             order.isStoreCredit ? 'SC' : 
                                                                             order.isFullRefund ? 'Full Refund' :
                                                                             order.refundAmount > 0 ? 'Partial Refund' :
                                                                             order.isCardPayment ? 'Card' : 'COD'}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ padding: '6px 8px' }}>{order.lineItemsCount}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}
            
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                <h3>Profitability Analysis - Click any row to see order details</h3>
                <ul style={{ marginLeft: '20px', lineHeight: '1.6', fontSize: '14px' }}>
                    <li><strong>Revenue:</strong> Actual realized income (current_total_price - refunds)</li>
                    <li><strong>COGS:</strong> Product costs from DB (delivered quantities only)</li>
                    <li><strong>Taxes:</strong> 22% VAT on realized income (excludes store credits)</li>
                    <li><strong>Shipping:</strong> 13.5 RON standard, 27 RON for refunded orders</li>
                    <li><strong>Fees:</strong> Card payment fees OR 5 RON bank transfer for refunds</li>
                    <li><strong>Void Costs:</strong> 13.5 RON shipping for COD orders not picked up</li>
                    <li><strong>Profit Before Ads:</strong> Key metric showing business profitability</li>
                    <li><strong>Final Profit:</strong> After ads cost and daily fixed costs (450 RON until 06/2025, 280 RON from 07/2025)</li>
                </ul>
            </div>
        </div>
    );
};

export default Stats4; 